import type { StyleSpecification, TerrainSpecification } from "mapbox-gl";

// Shared with map-scene.tsx's live setTerrain() toggle, so the preview
// reacts to the config switch with the exact spec baked into the style here.
export const TERRAIN_SPEC: TerrainSpecification = {
    source: "mapbox-dem",
    exaggeration: 1,
};

export function satelliteStyle(terrainEnabled: boolean): StyleSpecification {
    return {
        version: 8,
        glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
        sources: {
            "mapbox-satellite": {
                type: "raster",
                url: "mapbox://mapbox.satellite",
                tileSize: 512,
            },
            "mapbox-dem": {
                type: "raster-dem",
                url: "mapbox://mapbox.mapbox-terrain-dem-v1",
                // Lower resolution than the imagery source on purpose: this is
                // mostly open water with coastal relief, so the full 512px/z14
                // DEM pyramid buys nothing visually but costs a much heavier
                // per-tile mesh build during the idle wait every frame blocks on.
                tileSize: 256,
                maxzoom: 10,
            },
            "mapbox-streets": {
                type: "vector",
                url: "mapbox://mapbox.mapbox-streets-v8",
            },
        },
        layers: [
            {
                id: "background",
                type: "background",
                paint: { "background-color": "#0a1628" },
            },
            {
                id: "satellite",
                type: "raster",
                source: "mapbox-satellite",
                // Mapbox GL defaults to a 300ms cross-fade for newly-loaded raster
                // tiles, which is a pure paint-time animation — invisible to the
                // idle/isSourceLoaded checks delayRender waits on. Without this,
                // a tile can report "loaded" and still be captured mid-fade.
                paint: { "raster-opacity": 1, "raster-fade-duration": 0 },
            },
            {
                id: "landform-label",
                type: "symbol",
                source: "mapbox-streets",
                "source-layer": "natural_label",
                // Islands live under class "landform" in v8, with no field
                // to separate them from peaks/valleys/deserts/etc. sharing
                // that same class — there's no way to show archipelago
                // names (e.g. the Whitsundays) without also picking up
                // those other landform labels. Placed before water-label
                // (Mapbox gives collision priority to earlier layers) so
                // island names win the fight for space against broad sea/
                // bay names covering the same area, rather than losing it.
                filter: ["==", ["get", "class"], "landform"],
                layout: {
                    "text-field": ["get", "name"],
                    "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
                    "text-size": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        6,
                        17,
                        14,
                        24,
                    ],
                    "text-max-width": 8,
                    "text-padding": 1,
                    "symbol-sort-key": ["get", "sizerank"],
                },
                paint: {
                    "text-color": "#ffffff",
                    "text-halo-color": "rgba(0,5,20,0.7)",
                    "text-halo-width": 1.5,
                },
            },
            {
                id: "water-label",
                type: "symbol",
                source: "mapbox-streets",
                "source-layer": "natural_label",
                // v8's natural_label class list has no "lake"/"channel"/
                // "sound"/"strait"/"cove"/"inlet"/"fjord" values — those
                // feature types are folded into "sea" ("seas and other
                // large water features including some gulfs, straits,
                // bays, etc.") or "water" ("lakes, ponds, etc."). Filtering
                // on them was a silent no-op.
                filter: [
                    "in",
                    ["get", "class"],
                    ["literal", ["ocean", "sea", "bay", "river", "water", "canal", "dock", "reservoir"]],
                ],
                layout: {
                    "text-field": ["get", "name"],
                    "text-font": ["DIN Pro Italic", "Arial Unicode MS Regular"],
                    "text-size": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        2,
                        16,
                        10,
                        26,
                    ],
                    "text-max-width": 8,
                    "text-padding": 1,
                    "symbol-sort-key": ["get", "sizerank"],
                },
                paint: {
                    "text-color": "#a0c8e8",
                    "text-halo-color": "rgba(0,5,20,0.6)",
                    "text-halo-width": 1.5,
                },
            },
            {
                id: "country-label",
                type: "symbol",
                source: "mapbox-streets",
                "source-layer": "place_label",
                filter: ["==", ["get", "class"], "country"],
                layout: {
                    "text-field": ["get", "name"],
                    "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"],
                    "text-size": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        2,
                        18,
                        6,
                        32,
                    ],
                    "text-max-width": 8,
                    // place_label's rank field is "symbolrank", not
                    // "sizerank" (that's natural_label's field) — using the
                    // wrong name meant this always evaluated to null,
                    // silently defaulting collision priority instead of
                    // actually favoring more prominent places.
                    "symbol-sort-key": ["get", "symbolrank"],
                },
                paint: {
                    "text-color": "#ffffff",
                    "text-halo-color": "rgba(0,5,20,0.7)",
                    "text-halo-width": 2,
                },
            },
            {
                id: "state-label",
                type: "symbol",
                source: "mapbox-streets",
                "source-layer": "place_label",
                filter: ["==", ["get", "class"], "state"],
                layout: {
                    "text-field": ["get", "name"],
                    "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
                    "text-size": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        4,
                        17,
                        9,
                        28,
                    ],
                    "text-max-width": 8,
                    "text-transform": "uppercase",
                    "text-letter-spacing": 0.1,
                    "text-padding": 1,
                    // See the country-label comment above — place_label's
                    // rank field is "symbolrank", not "sizerank".
                    "symbol-sort-key": ["get", "symbolrank"],
                },
                paint: {
                    "text-color": "#e0e0e0",
                    "text-halo-color": "rgba(0,5,20,0.7)",
                    "text-halo-width": 1.5,
                },
            },
            {
                id: "place-label",
                type: "symbol",
                source: "mapbox-streets",
                "source-layer": "place_label",
                // place_label's "class" only ever holds "country" / "state"
                // / "settlement" / "settlement_subdivision" — city/town/
                // village/etc. are values of "type", a finer-grained field.
                // Filtering "class" against them was a silent no-op (and
                // "island"/"islet"/"archipelago" aren't place_label values
                // at all in v8 — see the landform-label layer below).
                //
                // "filterrank" (0-5, recalculated per zoom by Mapbox) is
                // the field the tileset docs specifically call out for
                // controlling label density: without it every non-
                // colliding settlement in view gets a label, which at a
                // wide zoom means every small inland suburb on screen —
                // capping it keeps only the locally-prominent places.
                filter: [
                    "all",
                    [
                        "in",
                        ["get", "type"],
                        [
                            "literal",
                            [
                                "city",
                                "town",
                                "village",
                                "hamlet",
                                "suburb",
                                "neighbourhood",
                            ],
                        ],
                    ],
                    ["<=", ["get", "filterrank"], 2],
                ],
                layout: {
                    "text-field": ["get", "name"],
                    "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
                    "text-size": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        6,
                        17,
                        14,
                        24,
                    ],
                    "text-max-width": 8,
                    "text-padding": 1,
                    // See the country-label comment above — place_label's
                    // rank field is "symbolrank", not "sizerank".
                    "symbol-sort-key": ["get", "symbolrank"],
                },
                paint: {
                    "text-color": "#ffffff",
                    "text-halo-color": "rgba(0,5,20,0.7)",
                    "text-halo-width": 1.5,
                },
            },
            {
                id: "poi-label",
                type: "symbol",
                source: "mapbox-streets",
                "source-layer": "poi_label",
                filter: [
                    "in",
                    ["get", "maki"],
                    ["literal", ["harbor", "beach"]],
                ],
                layout: {
                    "text-field": ["get", "name"],
                    "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
                    "text-size": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        10,
                        14,
                        16,
                        20,
                    ],
                    "text-max-width": 8,
                    "text-padding": 1,
                    "symbol-sort-key": ["get", "sizerank"],
                },
                paint: {
                    "text-color": "#a0c8e8",
                    "text-halo-color": "rgba(0,5,20,0.7)",
                    "text-halo-width": 1.5,
                },
            },
        ],
        ...(terrainEnabled ? { terrain: TERRAIN_SPEC } : {}),
        fog: {
            range: [0.5, 10],
            color: "#0a1628",
            "horizon-blend": 0.05,
        },
    };
}
